import * as yup from 'yup';
import type { CommissionPolicyScope, CommissionPolicyReferrerType } from '@/api/commission-policies';

export interface PolicyFormValues {
  policy_name: string;
  is_global: boolean;
  affiliate_ids: number[];
  scope: CommissionPolicyScope;
  commission_percent: number;
  product_group_id?: number;
  product_id?: number;
  effective_date: string;
  expired_date?: string;
  priority: number;
  description?: string;
  /** '' = everyone (no narrowing); null not allowed in form state, only in payload */
  referrer_type: CommissionPolicyReferrerType | '';
  department_ids: number[];
}

function todayISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export const policySchema = yup.object({
  policy_name: yup.string().required('Policy name is required').max(255),
  is_global: yup.boolean().default(false),
  affiliate_ids: yup
    .array()
    .of(yup.number().required())
    .default([])
    .when('is_global', {
      is: false,
      then: (s) => s.min(1, 'At least one affiliate owner is required'),
      otherwise: (s) => s.max(0, 'Global policies cannot have specific owners'),
    }),
  scope: yup
    .string()
    .oneOf(['default', 'product_group', 'product'] as const)
    .required('Scope is required'),
  commission_percent: yup
    .number()
    .required('Commission percent is required')
    .min(0, 'Min 0%')
    .max(100, 'Max 100%'),
  product_group_id: yup.number().when('scope', {
    is: 'product_group',
    then: (s) => s.required('Product group is required'),
    otherwise: (s) => s.nullable(),
  }),
  product_id: yup.number().when('scope', {
    is: 'product',
    then: (s) => s.required('Product is required'),
    otherwise: (s) => s.nullable(),
  }),
  effective_date: yup.string().required('Effective date is required'),
  expired_date: yup.string().nullable(),
  priority: yup.number().default(0),
  description: yup.string().nullable(),
  referrer_type: yup
    .string()
    .oneOf(['', 'partner', 'affiliate', 'employee', 'customer', 'other', 'reseller'] as const)
    .default(''),
  department_ids: yup
    .array()
    .of(yup.number().required())
    .default([])
    .when('referrer_type', {
      is: (v: string) => v !== 'employee',
      then: (s) => s.max(0, 'Departments require referrer type "Employee"'),
    }),
});

export function defaultPolicyValues(): PolicyFormValues {
  return {
    policy_name: '',
    is_global: false,
    affiliate_ids: [],
    scope: 'default',
    commission_percent: 5,
    product_group_id: undefined,
    product_id: undefined,
    effective_date: todayISO(),
    expired_date: undefined,
    priority: 0,
    description: '',
    referrer_type: '',
    department_ids: [],
  };
}
